import type { SeverityLevel } from '@sentry/browser';
import { ServerErrorModel } from '@sp-core/models/app-models/server-error.model';
import { ServerStatusCode } from '@sp-core/agreement-keys/server-status-code.enum';

export interface IServerErrorReport {
  message: string;
  level: SeverityLevel;
  fingerprint: string[];
  tags: Record<string, string>;
  extra: Record<string, unknown>;
}

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_SEGMENT = /^\d+$/;

/**
 * Turns a request URL into a stable endpoint name for grouping, e.g.
 * `https://api.pelletdose.co/api/v1/patients/0738f1f0-…/lab_draws/list?x=1` → `/patients/:id/lab_draws/list`.
 * Works for the relative `/api/v1/…` URLs used behind the dev server proxy too.
 */
export function normalizeApiPath(url: string | undefined): string {
  if (!url) {
    return 'unknown';
  }
  const path = url
    .replace(/^[a-z][a-z\d+.-]*:\/\/[^/]+/i, '')
    .replace(/[?#].*$/, '')
    .replace(/^\/?api\/v1(?=\/|$)/, '');
  const segments = path
    .split('/')
    .filter(Boolean)
    .map((segment) => (UUID_SEGMENT.test(segment) || NUMERIC_SEGMENT.test(segment) ? ':id' : segment));
  return `/${segments.join('/')}`;
}

/**
 * How an unhandled API error is reported to Sentry. Each status + endpoint gets its own issue,
 * except "no response" (status 0): one connection drop fails several requests at once, so all of
 * them share one issue and the endpoint stays searchable through the `api.endpoint` tag.
 */
export function buildServerErrorReport(error: ServerErrorModel): IServerErrorReport {
  const status = error.status;
  const method = error.method || 'UNKNOWN';
  const path = normalizeApiPath(error.url);
  const isNoResponse = status === ServerStatusCode.noResponse;

  return {
    message: isNoResponse ? 'API no response (status 0)' : `API ${status} ${method} ${path}`,
    // 0 and 4xx are network drops or answers the UI already handles; 5xx is a server failure.
    level: status >= 500 ? 'error' : 'warning',
    fingerprint: isNoResponse ? ['api-error', '0'] : ['api-error', String(status), method, path],
    tags: {
      'api.status': String(status),
      'api.endpoint': `${method} ${path}`,
    },
    extra: {
      serverMessage: error.message,
      validationErrors: error.validationErrors,
    },
  };
}
